import { useEffect, useId, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useProgramma } from "../ProgrammaShell";
import { RevertChangesDialog } from "../RevertChangesDialog";
import {
  computeProgrammeCompleteness,
  type EligiblePayload,
  type SelectionsPayload,
} from "../programme-completeness";
import { usePortalBootstrap, flowFlags, portalQueries, readPortalCache, writePortalCache } from "@/client/lib/portal";
import { invalidateSchool } from "@/client/lib/query";
import { Button } from "@/client/components/ui/button";
import { Badge } from "@/client/components/ui/badge";
import { Checkbox } from "@/client/components/ui/checkbox";
import { Notice } from "@/client/components/ui/notice";
import {
  CheckCircle2Icon,
  XCircleIcon,
  AlertTriangleIcon,
  AlertCircleIcon,
  UsersIcon,
  CalendarIcon,
  PlusIcon,
  InfoIcon,
  ArrowRightIcon,
  PencilIcon,
  RotateCcwIcon,
} from "lucide-react";

// UI Controleren category -> the server section code(s) the revert endpoint reverts. The
// Vast- & keuzeprogramma card spans two server sections (vast + keuze), so it reverts both.
const CATEGORY_SECTION_CODES: Record<string, string[]> = {
  vast_keuze: ["fixed_programme", "optional_programme"],
  technohub: ["technohub_programme"],
  deskundigheidsbevordering: ["deskundigheidsbevordering_programme"],
  maatwerk: ["maatwerk_programme"],
};

function formatSubmittedAt(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
  const time = d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
  return `${date} om ${time}`;
}

// ── Wire shapes (the slices this review reads from /selections + /eligible) ──────────────────────
type Selection = {
  id: number;
  offeringId: number;
  selectionStatusCode: string;
  selectionOriginCode: string;
  notes: string | null;
};
type SelectionGroup = {
  selectionId: number;
  classGroupId: number;
  includeFullGroup: boolean;
  requestedStudentCount: number | null;
};
type SelectionGroupYearLevel = { selectionId: number; classGroupId: number; yearLevelCode: string };
// Voorkeursperiode is PER (selection, group); phase-1's GET /selections puts classGroupId on every row.
type TimePreference = { id: number; selectionId: number; classGroupId: number; preferenceKindCode: string; rankOrder: number | null };
type TimePrefDateRange = { preferenceId: number; startDate: string; endDate: string };
// A row = the add-on is ANSWERED; `wanted` is the Ja (true) / Nee (false) answer; PER (selection, group).
type SelectionAddon = { selectionId: number; classGroupId: number; addonCode: string; wanted: boolean };
type OptOut = { classGroupId: number; enrolmentModeCode: string };

type ProgrammeResolution = {
  unresolved: { mode: "keuze" | "technohub"; groupNames: string[] }[];
  vastUnhandledCount: number;
  // Groups with a selected offering still missing an explicit answer (no voorkeursperiode / "geen
  // voorkeur", or an unanswered add-on) per the server — surfaced as open punten so the list is never empty.
  unansweredGroupNames: string[];
  // Optional school-wide sections KUVO offers that are neither chosen nor opted out of. Each blocks
  // aanleveren until the school picks >=1 or ticks "we doen dit jaar niet mee".
  undecidedSections: ("technohub" | "deskundigheidsbevordering" | "maatwerk")[];
  isComplete: boolean;
};

const OPTIONAL_SECTION_LABELS: Record<"technohub" | "deskundigheidsbevordering" | "maatwerk", string> = {
  technohub: "TechnoHub",
  deskundigheidsbevordering: "Deskundigheidsbevordering",
  maatwerk: "Extra maatwerk",
};

type Offering = { id: number; programmeId: number; providerOrganizationId: number; title: string; enrolmentModeCode: string };
type Programme = { id: number; title: string };
type Provider = { id: number; legalName: string; displayName: string | null };
type ClassGroupYearLevel = { groupId: number; displayName: string; yearLevelCode: string };
type OfferingAddon = { id: number; offeringId: number; addonCode: string; label: string };

const ylNum = (code: string) => parseInt(code.replace("groep_", ""), 10) || 99;

export function ProgrammaReviewSection({ schoolId }: { schoolId: number }) {
  const { refreshSections, sections } = useProgramma();
  const confirmId = useId();
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<"generic" | "no_changes" | "incomplete" | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [resolution, setResolution] = useState<ProgrammeResolution | null>(null);
  // The open revert confirm dialog: null = closed. "flow" reverts the whole programme;
  // "section" reverts one category (its server section codes). undefined codes = whole flow.
  const [revertTarget, setRevertTarget] = useState<{ scope: "section" | "flow"; sectionCodes?: string[] } | null>(null);

  // Seed from the prefetched/inlined cache for an instant first paint; load() revalidates.
  const cachedSel = readPortalCache<SelectionsPayload & {
    selections?: Selection[]; groups?: SelectionGroup[]; groupYearLevels?: SelectionGroupYearLevel[];
    timePreferences?: TimePreference[]; timePreferenceDateRanges?: TimePrefDateRange[];
    selectionAddons?: SelectionAddon[]; optOuts?: OptOut[]; sectionOptOuts?: string[];
  }>(portalQueries.selections(schoolId).queryKey);
  const cachedElig = readPortalCache<EligiblePayload & {
    offerings?: Offering[]; maatwerkOfferings?: Offering[]; deskundigheidsbevorderingOfferings?: Offering[];
    programmes?: Programme[]; providers?: Provider[]; classGroupYearLevels?: ClassGroupYearLevel[]; offeringAddons?: OfferingAddon[];
  }>(portalQueries.eligible(schoolId).queryKey);
  // Raw /eligible + /selections payloads, kept verbatim so the per-group completeness helper has
  // every field it needs (the parsed state below is shaped for the review list, not completeness).
  const [eligiblePayload, setEligiblePayload] = useState<EligiblePayload | null>(() => cachedElig ?? null);
  const [selectionsPayload, setSelectionsPayload] = useState<SelectionsPayload | null>(() => cachedSel ?? null);
  const [selections, setSelections] = useState<Selection[]>(() => cachedSel?.selections ?? []);
  const [selectionGroups, setSelectionGroups] = useState<SelectionGroup[]>(() => cachedSel?.groups ?? []);
  const [selectionGroupYearLevels, setSelectionGroupYearLevels] = useState<SelectionGroupYearLevel[]>(() => cachedSel?.groupYearLevels ?? []);
  const [timePreferences, setTimePreferences] = useState<TimePreference[]>(() => cachedSel?.timePreferences ?? []);
  const [timePrefDateRanges, setTimePrefDateRanges] = useState<TimePrefDateRange[]>(() => cachedSel?.timePreferenceDateRanges ?? []);
  const [selectionAddons, setSelectionAddons] = useState<SelectionAddon[]>(() => cachedSel?.selectionAddons ?? []);
  const [optOuts, setOptOuts] = useState<OptOut[]>(() => cachedSel?.optOuts ?? []);
  const [sectionOptOuts, setSectionOptOuts] = useState<string[]>(() => cachedSel?.sectionOptOuts ?? []);
  const [offerings, setOfferings] = useState<Offering[]>(() => cachedElig?.offerings ?? []);
  const [maatwerkOfferings, setMaatwerkOfferings] = useState<Offering[]>(() => cachedElig?.maatwerkOfferings ?? []);
  const [deskundigheidsbevorderingOfferings, setDeskundigheidsbevorderingOfferings] = useState<Offering[]>(() => cachedElig?.deskundigheidsbevorderingOfferings ?? []);
  const [programmes, setProgrammes] = useState<Programme[]>(() => cachedElig?.programmes ?? []);
  const [providers, setProviders] = useState<Provider[]>(() => cachedElig?.providers ?? []);
  const [classGroupYearLevels, setClassGroupYearLevels] = useState<ClassGroupYearLevel[]>(() => cachedElig?.classGroupYearLevels ?? []);
  const [offeringAddons, setOfferingAddons] = useState<OfferingAddon[]>(() => cachedElig?.offeringAddons ?? []);
  const [loading, setLoading] = useState(() => !(cachedSel && cachedElig));
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(false);
      const [selectionsRes, eligibleRes, resolutionRes] = await Promise.all([
        fetch(`/api/school/${schoolId}/selections`, { credentials: "include" }),
        fetch(`/api/school/${schoolId}/eligible`, { credentials: "include" }),
        fetch(`/api/school/${schoolId}/programme-resolution`, { credentials: "include" }),
      ]);
      if (resolutionRes.ok) setResolution(await resolutionRes.json());
      if (selectionsRes.ok) {
        const d = await selectionsRes.json();
        setSelectionsPayload(d);
        setSelections(d.selections ?? []);
        setSelectionGroups(d.groups ?? []);
        setSelectionGroupYearLevels(d.groupYearLevels ?? []);
        setTimePreferences(d.timePreferences ?? []);
        setTimePrefDateRanges(d.timePreferenceDateRanges ?? []);
        setSelectionAddons(d.selectionAddons ?? []);
        setOptOuts(d.optOuts ?? []);
        setSectionOptOuts(d.sectionOptOuts ?? []);
        writePortalCache(portalQueries.selections(schoolId).queryKey, d);
      }
      if (eligibleRes.ok) {
        const d = await eligibleRes.json();
        setEligiblePayload(d);
        setOfferings(d.offerings ?? []);
        setMaatwerkOfferings(d.maatwerkOfferings ?? []);
        setDeskundigheidsbevorderingOfferings(d.deskundigheidsbevorderingOfferings ?? []);
        setProgrammes(d.programmes ?? []);
        setProviders(d.providers ?? []);
        setClassGroupYearLevels(d.classGroupYearLevels ?? []);
        setOfferingAddons(d.offeringAddons ?? []);
        writePortalCache(portalQueries.eligible(schoolId).queryKey, d);
      }
    } catch {
      setError(true);
    }
    setLoading(false);
  }, [schoolId]);

  useEffect(() => { load(); }, [load]);

  // Flow state + deadline come from the shared portal bootstrap (cached), so this page
  // renders the right branch on first paint instead of fetching its own status.
  const { data: bootstrap } = usePortalBootstrap(schoolId);
  const flowState = bootstrap ? flowFlags(bootstrap.flows.programmaselectie) : null;
  const planningStarted = bootstrap?.planningStarted ?? false;

  // Once handed in with no further edits, there is nothing to submit again (D40).
  const alreadyDelivered = !!flowState?.everSubmitted && !flowState.hasUnsubmittedChanges;
  const hasChanges = !!flowState?.everSubmitted && flowState.hasUnsubmittedChanges;
  // Per-group completeness, recomputed HERE from the raw /eligible + /selections payloads using the
  // SAME shared rule the per-group selection section feeds its sub-nav icons (vast decision AND
  // keuze decision per dimension that has aanbod, plus any required voorkeursperiode). This is the
  // home of the completeness gate now; the selection section no longer blocks progress.
  const completeness = useMemo(
    () => computeProgrammeCompleteness(eligiblePayload, selectionsPayload, "vast_keuze"),
    [eligiblePayload, selectionsPayload],
  );
  // Gate: the SERVER resolution is authoritative (it is exactly what the submit endpoint re-checks),
  // so once it has loaded the button mirrors it; until then we fall back to the instant client
  // completeness so the button is not needlessly disabled. Every blocking reason (server unresolved
  // keuze groups + unanswered groups + vast backstop, merged below) is surfaced, so the button is
  // never disabled without a visible reason.
  const programmaComplete = resolution ? resolution.isComplete : completeness.allResolved;
  const canSubmit = confirmed && !alreadyDelivered && programmaComplete;
  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Freeze a full version snapshot of the whole aanlevering (programma flow).
      const res = await fetch(`/api/school/${schoolId}/submit-version`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ flow: "programmaselectie", confirmed: true }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        if (res.status === 409 && body?.error === "no_changes") {
          setSubmitError("no_changes");
          invalidateSchool(schoolId);
          refreshSections();
        } else if (res.status === 409 && body?.error === "programma_incomplete") {
          setSubmitError("incomplete");
          setResolution({ unresolved: body.unresolved ?? [], unansweredGroupNames: body.unansweredGroupNames ?? [], vastUnhandledCount: body.vastUnhandledCount ?? 0, undecidedSections: body.undecidedSections ?? [], isComplete: false });
        } else {
          setSubmitError("generic");
        }
        setSubmitting(false);
        return;
      }
      setJustSubmitted(true);
      invalidateSchool(schoolId);
      // Re-pull the stepper (built from a manual /progress fetch, not a query observer) so
      // "Programma controleren" flips to done after submit.
      refreshSections();
    } catch {
      setSubmitError("generic");
      setSubmitting(false);
    }
  };

  // After a revert: invalidate the school cache (bootstrap/overview drive the flow + per-section
  // delivery state) and re-pull the stepper + this page's selections so the reverted category/flow
  // flips back to green and the review list shows the restored picks.
  const handleReverted = async () => {
    invalidateSchool(schoolId);
    await Promise.all([refreshSections(), load()]);
  };

  // Per-category "Gewijzigd" state, read from the programma stepper's per-section deliveryState.
  // A category offers a per-section revert only when it changed since the last aanlevering (and a
  // version exists). Keyed by the UI category code that maps onto CATEGORY_SECTION_CODES.
  const changedByCategory = useMemo(() => {
    const m: Record<string, boolean> = {};
    if (flowState?.everSubmitted) {
      for (const s of sections) m[s.code] = s.deliveryState === "changed";
    }
    return m;
  }, [sections, flowState?.everSubmitted]);

  // ── Lookup maps (offering -> programme/provider/mode, group name + leerjaar, voorkeur, add-ons) ──
  const allOfferings = useMemo(() => [...offerings, ...maatwerkOfferings, ...deskundigheidsbevorderingOfferings], [offerings, maatwerkOfferings, deskundigheidsbevorderingOfferings]);
  const programmeById = useMemo(() => new Map(programmes.map((p) => [p.id, p])), [programmes]);
  const providerById = useMemo(() => new Map(providers.map((p) => [p.id, p])), [providers]);
  const offeringById = useMemo(() => new Map(allOfferings.map((o) => [o.id, o])), [allOfferings]);
  const offeringTitle = useCallback((offeringId: number) => {
    const o = offeringById.get(offeringId);
    const p = o ? programmeById.get(o.programmeId) : null;
    return p?.title ?? o?.title ?? `Programma #${offeringId}`;
  }, [offeringById, programmeById]);
  const offeringProvider = useCallback((offeringId: number) => {
    const o = offeringById.get(offeringId);
    const pr = o ? providerById.get(o.providerOrganizationId) : null;
    return pr ? (pr.displayName ?? pr.legalName) : null;
  }, [offeringById, providerById]);
  const offeringMode = useCallback((offeringId: number) => offeringById.get(offeringId)?.enrolmentModeCode ?? null, [offeringById]);

  // Group display name + its leerjaar codes (so we can name combi-/vast leerjaren explicitly).
  const groupInfo = useMemo(() => {
    const m = new Map<number, { displayName: string; codes: string[] }>();
    for (const r of classGroupYearLevels) {
      if (!m.has(r.groupId)) m.set(r.groupId, { displayName: r.displayName, codes: [] });
      m.get(r.groupId)!.codes.push(r.yearLevelCode);
    }
    return m;
  }, [classGroupYearLevels]);
  const groupName = useCallback((groupId: number) => groupInfo.get(groupId)?.displayName ?? `Groep ${groupId}`, [groupInfo]);

  // selectionId -> the leerjaren chosen per group (combi/vast). Empty = the whole group takes part.
  const selectionGroupYLs = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of selectionGroupYearLevels) {
      const k = `${r.selectionId}:${r.classGroupId}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r.yearLevelCode);
    }
    return m;
  }, [selectionGroupYearLevels]);

  const groupsBySelection = useMemo(() => {
    const m = new Map<number, SelectionGroup[]>();
    for (const g of selectionGroups) {
      if (!m.has(g.selectionId)) m.set(g.selectionId, []);
      m.get(g.selectionId)!.push(g);
    }
    return m;
  }, [selectionGroups]);

  // `${selectionId}:${classGroupId}` -> that group's chosen voorkeursperiodes (preferred date ranges,
  // ranked), formatted as text. Per-group now: phase-1 puts classGroupId on every preference row, so
  // two groups doing the same activity each carry their own voorkeur.
  const rangeByPref = useMemo(() => new Map(timePrefDateRanges.map((r) => [r.preferenceId, r])), [timePrefDateRanges]);
  const voorkeurBySelGroup = useMemo(() => {
    const m = new Map<string, string[]>();
    const rows = timePreferences
      .filter((tp) => tp.preferenceKindCode === "preferred")
      .sort((a, b) => (a.rankOrder ?? 9999) - (b.rankOrder ?? 9999) || a.id - b.id);
    for (const tp of rows) {
      const r = rangeByPref.get(tp.id);
      if (!r) continue;
      // Blocks are monthly, so we show the MONTH-YEAR label ("November 2026") derived from the
      // range's startDate, not the raw "1 november t/m 30 november" span. Capitalize the first letter
      // (toLocaleDateString gives a lowercase Dutch month name).
      const my = new Date(r.startDate).toLocaleDateString("nl-NL", { month: "long", year: "numeric" });
      const label = my.charAt(0).toUpperCase() + my.slice(1);
      const k = `${tp.selectionId}:${tp.classGroupId}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(label);
    }
    return m;
  }, [timePreferences, rangeByPref]);
  // `${selectionId}:${classGroupId}` -> has the explicit "Geen voorkeur" answer for THAT group (a
  // geen_voorkeur marker, no date-range). Surfaced at Controleren so the school sees its deliberate
  // "het maakt niet uit" answer, distinct from "nog geen voorkeur opgegeven".
  const geenVoorkeurBySelGroup = useMemo(() => {
    const s = new Set<string>();
    for (const tp of timePreferences) if (tp.preferenceKindCode === "geen_voorkeur") s.add(`${tp.selectionId}:${tp.classGroupId}`);
    return s;
  }, [timePreferences]);

  // `${selectionId}:${classGroupId}` -> THAT group's ANSWERED optional add-ons, each as { label, wanted }
  // (Ja/Nee), so the Controleren overzicht reflects every add-on the school answered (not only the "Ja"
  // ones). Add-ons the school left unanswered have no row, so they simply do not appear.
  const addonLabelByCode = useMemo(() => new Map(offeringAddons.map((a) => [`${a.offeringId}:${a.addonCode}`, a.label])), [offeringAddons]);
  const addonsBySelGroup = useMemo(() => {
    const m = new Map<string, { label: string; wanted: boolean }[]>();
    const offeringOf = new Map(selections.map((s) => [s.id, s.offeringId]));
    for (const sa of selectionAddons) {
      const offId = offeringOf.get(sa.selectionId);
      const label = offId != null ? addonLabelByCode.get(`${offId}:${sa.addonCode}`) ?? sa.addonCode : sa.addonCode;
      const k = `${sa.selectionId}:${sa.classGroupId}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push({ label, wanted: sa.wanted });
    }
    return m;
  }, [selectionAddons, selections, addonLabelByCode]);

  // An auto-assigned selection ('auto_vast' / 'auto_keuze') is one KUVO vinkte automatically aan
  // (opt-out model). We surface those here as "automatisch geselecteerd" so auto-selection is
  // visible at Controleren, never hidden. A deliberate school re-pick becomes 'school_chosen'.
  const isAuto = (origin: string) => origin === "auto_vast" || origin === "auto_keuze";

  // A selection's lowest leerjaar across the group(s) it belongs to: the minimum ylNum over every
  // leerjaar of every group on the selection (Combi 1/2 -> 1, Groep 3 -> 3, Combi 6/7 -> 6). Used to
  // order the review rows by leerjaar; a selection with no groups sorts last (99).
  const selectionLowestLeerjaar = useCallback((selectionId: number) => {
    const grps = groupsBySelection.get(selectionId) ?? [];
    const codes = grps.flatMap((g) => groupInfo.get(g.classGroupId)?.codes ?? []);
    return codes.length ? Math.min(...codes.map(ylNum)) : 99;
  }, [groupsBySelection, groupInfo]);
  // The selection's group names joined (nl, numeric) — the leerjaar-tie tiebreaker for the row order.
  const selectionGroupNames = useCallback((selectionId: number) =>
    (groupsBySelection.get(selectionId) ?? [])
      .map((g) => groupName(g.classGroupId))
      .sort((a, b) => a.localeCompare(b, "nl", { numeric: true }))
      .join(", "),
    [groupsBySelection, groupName],
  );
  // Per category, the live (non-cancelled) selections, ordered by the lowest leerjaar of the group(s)
  // they belong to (ascending), ties by group name (nl, numeric), then programme title as a final
  // stable tiebreaker.
  const byMode = useCallback((mode: string) =>
    selections
      .filter((s) => s.selectionStatusCode !== "cancelled" && offeringMode(s.offeringId) === mode)
      .sort((a, b) =>
        selectionLowestLeerjaar(a.id) - selectionLowestLeerjaar(b.id)
        || selectionGroupNames(a.id).localeCompare(selectionGroupNames(b.id), "nl", { numeric: true })
        || offeringTitle(a.offeringId).localeCompare(offeringTitle(b.offeringId), "nl", { numeric: true })),
    [selections, offeringMode, offeringTitle, selectionLowestLeerjaar, selectionGroupNames],
  );
  const vast = useMemo(() => byMode("vast"), [byMode]);
  const keuze = useMemo(() => byMode("keuze"), [byMode]);
  const technohub = useMemo(() => byMode("technohub"), [byMode]);
  const deskundigheidsbevordering = useMemo(() => byMode("deskundigheidsbevordering"), [byMode]);
  const maatwerk = useMemo(() => byMode("maatwerk"), [byMode]);

  // Groups that opted OUT of the school-chosen categories this year (keuze / technohub).
  const optedOutGroups = useCallback((mode: "keuze" | "technohub") =>
    optOuts
      .filter((o) => o.enrolmentModeCode === mode)
      .map((o) => groupName(o.classGroupId))
      .sort((a, b) => a.localeCompare(b, "nl", { numeric: true })),
    [optOuts, groupName],
  );
  const keuzeOptedOut = useMemo(() => optedOutGroups("keuze"), [optedOutGroups]);
  const technohubOptedOut = useMemo(() => optedOutGroups("technohub"), [optedOutGroups]);
  const deskundigheidsbevorderingOptedOut = sectionOptOuts.includes("deskundigheidsbevordering");
  const maatwerkOptedOut = sectionOptOuts.includes("maatwerk");

  // One group's badge: its name, the partial-combi leerjaren (only when it is a partial pick), and a
  // requested-student-count suffix when the group is not taking part in full. Shared by the single-
  // and multi-group layouts so a group reads identically either way.
  const renderGroupBadge = useCallback((selectionId: number, g: SelectionGroup) => {
    const codes = (selectionGroupYLs.get(`${selectionId}:${g.classGroupId}`) ?? []).slice().sort((a, b) => ylNum(a) - ylNum(b));
    const totalCodes = groupInfo.get(g.classGroupId)?.codes.length ?? 0;
    // Only spell out the leerjaren when it is a partial pick of a combi group (clarity without noise).
    const showLeerjaren = codes.length > 0 && codes.length < totalCodes;
    return (
      <Badge key={g.classGroupId} variant="outline" size="sm">
        {groupName(g.classGroupId)}
        {showLeerjaren && (
          <span className="ml-0.5 text-muted-foreground">
            (leerjaar {codes.map((c) => ylNum(c)).join(", ")})
          </span>
        )}
        {!g.includeFullGroup && g.requestedStudentCount != null && (
          <span className="ml-0.5 text-muted-foreground">({g.requestedStudentCount} ll.)</span>
        )}
      </Badge>
    );
  }, [selectionGroupYLs, groupInfo, groupName]);

  // The voorkeursperiode + add-on lines for ONE (selection, group): the group's chosen periods (or its
  // explicit "Geen voorkeur"), then each answered add-on with its Ja/Nee. Reused for every group, so
  // each group on a multi-group selection shows its OWN voorkeur/add-ons (phase-1 keys these per group).
  const renderGroupAnswers = useCallback((selectionId: number, classGroupId: number) => {
    const voorkeur = voorkeurBySelGroup.get(`${selectionId}:${classGroupId}`) ?? [];
    const geenVoorkeur = geenVoorkeurBySelGroup.has(`${selectionId}:${classGroupId}`);
    const addons = addonsBySelGroup.get(`${selectionId}:${classGroupId}`) ?? [];
    if (voorkeur.length === 0 && !geenVoorkeur && addons.length === 0) return null;
    return (
      <>
        {/* Voorkeursperiode(s) — the chosen periods, or the explicit "Geen voorkeur" answer. */}
        {(voorkeur.length > 0 || geenVoorkeur) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <CalendarIcon size={13} className="shrink-0 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Voorkeursperiode:</span>
            {geenVoorkeur ? (
              <Badge variant="outline" size="sm">Geen voorkeur</Badge>
            ) : (
              voorkeur.map((v, i) => (
                <Badge key={i} variant="secondary" size="sm">{v}</Badge>
              ))
            )}
          </div>
        )}

        {/* Extra's (add-ons) — each answered add-on with its Ja/Nee antwoord. */}
        {addons.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <PlusIcon size={13} className="shrink-0 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Extra:</span>
            {addons.map((a, i) => (
              <Badge key={i} variant={a.wanted ? "secondary" : "outline"} size="sm">
                {a.label}: {a.wanted ? "ja" : "nee"}
              </Badge>
            ))}
          </div>
        )}
      </>
    );
  }, [voorkeurBySelGroup, geenVoorkeurBySelGroup, addonsBySelGroup]);

  // ── A single selection row, shared by every category, showing all relevant info ────────────────
  // Group-centric: the activity title appears ONCE, then each group's voorkeur/add-ons are shown under
  // that group. A single-group selection renders exactly as before (group badge on its own line,
  // voorkeur/add-ons beneath). A selection spanning multiple groups gets a per-group breakdown so each
  // group's voorkeursperiode + add-on antwoorden are unambiguous.
  const renderSelection = useCallback((s: Selection) => {
    const grps = (groupsBySelection.get(s.id) ?? [])
      .slice()
      .sort((a, b) => groupName(a.classGroupId).localeCompare(groupName(b.classGroupId), "nl", { numeric: true }));
    const multiGroup = grps.length > 1;
    return (
      <div key={s.id} className="px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{offeringTitle(s.offeringId)}</p>
            {offeringProvider(s.offeringId) && (
              <p className="text-xs text-muted-foreground">{offeringProvider(s.offeringId)}</p>
            )}
          </div>
        </div>

        {multiGroup ? (
          // One block per group: the group badge, then THAT group's voorkeur/add-ons indented beneath.
          <div className="mt-2 space-y-3">
            {grps.map((g) => (
              <div key={g.classGroupId} className="border-l-2 border-border pl-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  <UsersIcon size={13} className="shrink-0 text-muted-foreground" />
                  {renderGroupBadge(s.id, g)}
                </div>
                {renderGroupAnswers(s.id, g.classGroupId)}
              </div>
            ))}
          </div>
        ) : (
          // Single group: keep the original flat layout (group badge line + voorkeur/add-ons beneath).
          <>
            {grps.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <UsersIcon size={13} className="shrink-0 text-muted-foreground" />
                {grps.map((g) => renderGroupBadge(s.id, g))}
              </div>
            )}
            {grps.length === 1 && grps[0] && renderGroupAnswers(s.id, grps[0].classGroupId)}
          </>
        )}

        {/* Vrije toelichting (deskundigheidsbevordering / maatwerk) */}
        {s.notes && <p className="mt-1.5 text-sm text-muted-foreground italic">“{s.notes}”</p>}
      </div>
    );
  }, [groupsBySelection, groupName, offeringTitle, offeringProvider, renderGroupBadge, renderGroupAnswers]);

  // What still blocks aanleveren, with a link back to the step where it is fixed. One consolidated
  // "Let op" notice below. The per-group "Nog open" list is CLIENT-derived (computeProgramme
  // completeness over the same /eligible + /selections data the selection section uses), so it
  // names exactly the groups whose vaste of keuze beslissing nog ontbreekt, of die nog een
  // verplichte voorkeursperiode missen. The server's vastUnhandledCount stays as a backstop.
  const vastKeuzeHref = `/school/${schoolId}/programmaselectie/vast_keuze`;
  const blockingItems: { text: string; href: string }[] = [];
  // Open groups come from the AUTHORITATIVE server resolution only (it is exactly what the submit
  // endpoint checks): unresolved keuze groups + groups with an unanswered selected offering. The
  // client completeness (which drives the per-group subnav icons) is NOT used here: it can briefly
  // diverge/be stale and would otherwise name groups the server already considers complete.
  const openGroupNames = new Set<string>();
  for (const u of resolution?.unresolved ?? []) for (const n of u.groupNames) openGroupNames.add(n);
  for (const n of resolution?.unansweredGroupNames ?? []) openGroupNames.add(n);
  if (openGroupNames.size > 0) {
    const names = [...openGroupNames];
    blockingItems.push({
      text: `${names.length} groep${names.length !== 1 ? "en" : ""} nog niet volledig ingevuld. Nog open: ${names.join(", ")}.`,
      href: vastKeuzeHref,
    });
  }
  if ((resolution?.vastUnhandledCount ?? 0) > 0) {
    blockingItems.push({
      text: `${resolution!.vastUnhandledCount} vast programma nog niet bevestigd of afgemeld.`,
      href: vastKeuzeHref,
    });
  }
  // Optional school-wide sections that still need an explicit keuze-of-niet (server-authoritative).
  for (const m of resolution?.undecidedSections ?? []) {
    blockingItems.push({
      text: `${OPTIONAL_SECTION_LABELS[m]}: kies minstens een optie of geef aan dat uw school dit jaar niet meedoet.`,
      href: `/school/${schoolId}/programmaselectie/${m}`,
    });
  }

  // The "aangeleverd" decision only needs the bootstrap flow state (cached/inlined, so
  // instant). Render it BEFORE the selections/eligible load gate, so a delivered programme
  // shows its card immediately instead of waiting for data it does not need.
  if (justSubmitted) {
    return (
      <ProgrammaDeliveredCard
        schoolId={schoolId}
        title="Programma aangeleverd"
        body="Bedankt. KUVO neemt uw programmakeuzes door bij het maken van de planning. U kunt uw keuzes altijd aanpassen en opnieuw aanleveren; KUVO gebruikt dan de nieuwste versie."
      />
    );
  }

  // Already delivered, nothing changed since: no submit button (D40).
  if (alreadyDelivered) {
    return (
      <ProgrammaDeliveredCard
        schoolId={schoolId}
        title="Programma aangeleverd"
        body={
          flowState?.lastSubmittedAt
            ? `Aangeleverd op ${formatSubmittedAt(flowState.lastSubmittedAt)}. Er zijn sindsdien geen wijzigingen, dus u hoeft niets opnieuw aan te leveren. Wilt u iets aanpassen? Wijzig uw keuzes in de stappen hiernaast; daarna kunt u opnieuw aanleveren.`
            : "U heeft uw programmakeuzes aangeleverd. Wilt u iets aanpassen? Wijzig uw keuzes in de stappen hiernaast; daarna kunt u opnieuw aanleveren."
        }
      />
    );
  }

  // Full review needs the selections/eligible data; only this path waits for it.
  if (loading || flowState === null) return <p className="text-sm text-muted-foreground">Laden...</p>;

  if (error) {
    return (
      <Notice
        tone="error"
        icon={<AlertCircleIcon />}
        title="Kan de gegevens niet laden"
        action={
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}>
            Opnieuw proberen
          </Button>
        }
      >
        Controleer uw internetverbinding en probeer het opnieuw.
      </Notice>
    );
  }

  const totalChosen = vast.length + keuze.length + technohub.length + deskundigheidsbevordering.length + maatwerk.length;

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Controleer uw programmakeuzes voordat u ze aanlevert. KUVO gebruikt deze keuzes om de planning te maken.
      </p>

      {/* Vast- & keuzeprogramma (vaste voorstellingen + keuzeprogramma's) */}
      <ReviewCategory
        title="Vast- & keuzeprogramma"
        action={changedByCategory.vast_keuze ? <RevertCategoryLink onClick={() => setRevertTarget({ scope: "section", sectionCodes: CATEGORY_SECTION_CODES.vast_keuze })} /> : undefined}
      >
        {vast.length === 0 && keuze.length === 0 && keuzeOptedOut.length === 0 ? (
          <EmptyLine text="Nog geen vast- of keuzeprogramma gekozen." />
        ) : (
          <div className="divide-y divide-border rounded-lg border">
            {vast.map(renderSelection)}
            {keuze.map(renderSelection)}
            {keuzeOptedOut.length > 0 && <OptedOutLine label="Doet niet mee aan het keuzeprogramma" groups={keuzeOptedOut} />}
          </div>
        )}
      </ReviewCategory>

      {/* TechnoHub */}
      <ReviewCategory
        title="TechnoHub"
        action={changedByCategory.technohub ? <RevertCategoryLink onClick={() => setRevertTarget({ scope: "section", sectionCodes: CATEGORY_SECTION_CODES.technohub })} /> : undefined}
      >
        {technohub.length === 0 && technohubOptedOut.length === 0 ? (
          <EmptyLine text="Nog geen TechnoHub-workshops gekozen." />
        ) : (
          <div className="divide-y divide-border rounded-lg border">
            {technohub.map(renderSelection)}
            {technohubOptedOut.length > 0 && <OptedOutLine label="Doet niet mee aan TechnoHub" groups={technohubOptedOut} />}
          </div>
        )}
      </ReviewCategory>

      {/* Deskundigheidsbevordering */}
      <ReviewCategory
        title="Deskundigheidsbevordering"
        action={changedByCategory.deskundigheidsbevordering ? <RevertCategoryLink onClick={() => setRevertTarget({ scope: "section", sectionCodes: CATEGORY_SECTION_CODES.deskundigheidsbevordering })} /> : undefined}
      >
        {deskundigheidsbevorderingOptedOut ? (
          <SectionOptedOutLine text="Uw school neemt dit schooljaar geen deskundigheidsbevordering af." />
        ) : deskundigheidsbevordering.length === 0 ? (
          <EmptyLine text="Nog geen training gekozen." />
        ) : (
          <div className="divide-y divide-border rounded-lg border">
            {deskundigheidsbevordering.map(renderSelection)}
          </div>
        )}
      </ReviewCategory>

      {/* Extra maatwerk */}
      <ReviewCategory
        title="Extra maatwerk"
        action={changedByCategory.maatwerk ? <RevertCategoryLink onClick={() => setRevertTarget({ scope: "section", sectionCodes: CATEGORY_SECTION_CODES.maatwerk })} /> : undefined}
      >
        {maatwerkOptedOut ? (
          <SectionOptedOutLine text="Uw school neemt dit schooljaar geen extra maatwerk af." />
        ) : maatwerk.length === 0 ? (
          <EmptyLine text="Nog geen maatwerk aangevraagd." />
        ) : (
          <div className="divide-y divide-border rounded-lg border">
            {maatwerk.map(renderSelection)}
          </div>
        )}
      </ReviewCategory>

      {/* Wat aanleveren nog blokkeert — server-derived, one-liner per reden (alert-warning-link-colored) */}
      {blockingItems.map((b, i) => (
        <div key={i} className="rounded-md border border-amber-500/50 px-4 py-3 text-amber-600">
          <div className="flex gap-3">
            <AlertTriangleIcon aria-hidden className="mt-0.5 shrink-0 opacity-60" size={16} />
            <div className="flex grow justify-between gap-3">
              <p className="text-sm">{b.text}</p>
              <Link to={b.href} className="group whitespace-nowrap text-sm font-medium hover:underline">
                Aanpassen
                <ArrowRightIcon aria-hidden className="-mt-0.5 ms-1 inline-flex opacity-60 transition-transform group-hover:translate-x-0.5" size={16} />
              </Link>
            </div>
          </div>
        </div>
      ))}

      {totalChosen === 0 && blockingItems.length === 0 && (
        <div className="rounded-lg border border-border bg-muted/30 px-6 py-8 text-center">
          <InfoIcon className="mx-auto text-muted-foreground" size={22} />
          <p className="mt-2 text-sm text-muted-foreground">
            Geen enkele groep doet dit jaar mee aan het keuze- of TechnoHub-programma. U kunt het programma zo aanleveren.
          </p>
        </div>
      )}

      {/* Submit */}
      <div className="space-y-4 pt-5">
        {((hasChanges && flowState.lastSubmittedAt) || planningStarted) && (
          <Notice
            tone={flowState.dependencyChanged || planningStarted ? "warning" : "info"}
            icon={planningStarted ? <AlertCircleIcon /> : <PencilIcon />}
            title="Let op voordat u aanlevert"
          >
            <ul className="mt-1 space-y-1">
              {hasChanges && flowState.dependencyChanged && (
                <li>U heeft groepen of leerlingaantallen aangepast na uw programmakeuze. Lever opnieuw in zodat KUVO met de juiste aantallen plant.</li>
              )}
              {hasChanges && !flowState.dependencyChanged && flowState.lastSubmittedAt && (
                <li>Laatst ingeleverd op {formatSubmittedAt(flowState.lastSubmittedAt)}. Lever opnieuw in om uw wijzigingen door te geven.</li>
              )}
              {planningStarted && (
                <li>De planning is al begonnen; wijzigingen kunnen mogelijk niet meer worden verwerkt.</li>
              )}
            </ul>
          </Notice>
        )}

        {/* "incomplete" intentionally renders no separate notice: when something blocks, the amber
            open-punten list above already names the exact groups (and the button stays disabled). */}
        {submitError === "no_changes" && (
          <Notice tone="error" icon={<AlertCircleIcon />} title="Er zijn geen wijzigingen om aan te leveren">
            Uw programma is al aangeleverd.
          </Notice>
        )}
        {submitError === "generic" && (
          <Notice tone="error" icon={<AlertCircleIcon />} title="Het aanleveren is niet gelukt">
            Controleer uw internetverbinding en probeer het opnieuw.
          </Notice>
        )}

        <div>
          <label htmlFor={confirmId} className="flex gap-3">
            <Checkbox id={confirmId} checked={confirmed} onCheckedChange={(val) => setConfirmed(val === true)} className="mt-0.5 shrink-0" />
            <span className="text-sm leading-5">Ik heb de programmakeuzes nagekeken en het klopt.</span>
          </label>
          <p className="mt-3 text-xs text-muted-foreground">
            KUVO verwerkt deze gegevens zoals beschreven in de{" "}
            <Link to="/privacyverklaring" target="_blank" className="cursor-pointer font-medium text-primary hover:underline">
              privacyverklaring
            </Link>.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {totalChosen > 0
              ? `${totalChosen} programma${totalChosen !== 1 ? "'s" : ""} klaar om aan te leveren`
              : "Klaar om aan te leveren"}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {/* Whole-flow "Wijzigingen terugzetten": only when there is something to revert (pending
                changes) AND a version exists to revert to. Secondary, beside the primary button. */}
            {hasChanges && (
              <Button
                size="lg"
                variant="outline"
                onClick={() => setRevertTarget({ scope: "flow" })}
                disabled={submitting}
              >
                <RotateCcwIcon aria-hidden size={16} className="-ms-0.5 opacity-70" />
                Wijzigingen terugzetten
              </Button>
            )}
            <Button size="lg" onClick={handleSubmit} disabled={!canSubmit || submitting}>
              {submitting ? "Bezig met aanleveren..." : hasChanges ? "Wijzigingen aanleveren" : "Programma aanleveren"}
            </Button>
          </div>
        </div>
      </div>

      {revertTarget && (
        <RevertChangesDialog
          open
          onOpenChange={(o) => { if (!o) setRevertTarget(null); }}
          schoolId={schoolId}
          flow="programmaselectie"
          sectionCodes={revertTarget.sectionCodes}
          scope={revertTarget.scope}
          lastSubmittedAt={flowState?.lastSubmittedAt ?? null}
          onReverted={handleReverted}
        />
      )}
    </div>
  );
}

// One nav-matching category heading + its body (the offering list, an empty line, or an opt-out
// line). `action` is an optional control beside the heading (the per-section "terugzetten" link).
function ReviewCategory({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs font-semibold text-muted-foreground">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

// The per-section "Wijzigingen terugzetten" link shown beside a changed category heading.
function RevertCategoryLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex shrink-0 cursor-pointer items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
    >
      <RotateCcwIcon aria-hidden size={12} className="opacity-70" />
      Wijzigingen terugzetten
    </button>
  );
}

function EmptyLine({ text }: { text: string }) {
  return <p className="text-sm text-muted-foreground">{text}</p>;
}

// A "doet niet mee"-line inside a category list, naming the groups that opted out (keuze / technohub).
function OptedOutLine({ label, groups }: { label: string; groups: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-4 py-3">
      <XCircleIcon size={13} className="shrink-0 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">{label}:</span>
      {groups.map((g) => (
        <Badge key={g} variant="outline" size="sm">{g}</Badge>
      ))}
    </div>
  );
}

// A school-wide "doet niet mee"-line for the two optional sections (deskundigheidsbevordering / maatwerk).
function SectionOptedOutLine({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-4 py-3">
      <XCircleIcon size={14} className="shrink-0 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">{text}</span>
    </div>
  );
}

function ProgrammaDeliveredCard({ schoolId, title, body }: { schoolId: number; title: string; body: string }) {
  return (
    <div className="rounded-lg border p-4 shadow-xs">
      <div className="flex gap-3">
        <div aria-hidden="true" className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 max-md:mt-0.5">
          <CheckCircle2Icon className="text-emerald-600" size={18} />
        </div>
        <div className="flex grow flex-col gap-3">
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">{title}</p>
            <p className="text-sm text-muted-foreground">{body}</p>
          </div>
          <div>
            <Button size="sm" variant="outline" render={<Link to={`/school/${schoolId}/overzicht`} />}>
              Naar overzicht
              <ArrowRightIcon size={14} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
